/*
 * The payout-matrix grid and its prize-tier modal, shared by the two surfaces that edit a matrix:
 * the Prize Tiers page (the `default_payout_matrix` every new Contest snapshots, CAE-513) and the
 * contest editor's Prizes step (that Contest's own snapshot, CAE-298). One component, because the
 * two must not drift: an operator sets the defaults here and reads them back there.
 *
 * Rows are correct-count tiers 1..maxPicks — any tier can pay (ADR-0002, GL-14), never a hardcoded
 * 4/5/6. Columns are the segments (Default plus the segment registry), in the shared
 * `payoutMatrixSegments` order the detail page's read view also uses.
 */
import { useState, type ReactNode } from 'react';
import {
  Button,
  Card,
  Input,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
  RewardPill,
  Select,
  TCell,
  THead,
  TRow,
  Table,
} from '@cou/admin-ui';
import {
  ParlayEscalatorFields,
  escalatorProblems,
  toEscalatorCell,
  toEscalatorDraft,
  type EscalatorDraft,
} from './ParlayEscalatorFields';
import { payoutMatrixSegments, type PayoutMatrixDraft } from './payout-matrix';
import { formatRewardValue, toRewardType, type RewardCell } from './reward-cell';

export interface PayoutMatrixEditorProps {
  readonly matrix: PayoutMatrixDraft;
  readonly segmentRegistry: Record<string, string>;
  readonly maxPicks: number;
  readonly eyebrow: string;
  readonly title: string;
  /** Right-hand header slot — the Prize Tiers page names where its row count comes from. */
  readonly action?: ReactNode;
  /** Copy for an unset cell: "+ Add tier" per Contest, "+ Add prize" on the defaults. */
  readonly emptyLabel: string;
  readonly hint: ReactNode;
  /** Omitted when the operator may not edit (`config:view` only): cells render as plain text. */
  readonly onEditCell?: (segment: string, tier: string) => void;
}

export function PayoutMatrixEditor({
  matrix,
  segmentRegistry,
  maxPicks,
  eyebrow,
  title,
  action,
  emptyLabel,
  hint,
  onEditCell,
}: PayoutMatrixEditorProps) {
  const segments = payoutMatrixSegments(segmentRegistry);
  const tiers = Array.from({ length: Math.max(maxPicks, 1) }, (_, i) => String(maxPicks - i));

  return (
    <Card eyebrow={eyebrow} title={title} action={action}>
      <Table scrollable columns={`140px repeat(${segments.length}, minmax(110px,1fr))`}>
        <THead>
          <TCell>Correct picks</TCell>
          {segments.map((segment) => (
            <TCell key={segment}>
              {segment === 'default' ? 'Default' : segmentRegistry[segment]}
            </TCell>
          ))}
        </THead>
        {tiers.map((tier) => (
          <TRow key={tier}>
            <TCell variant="primary">
              <span className="font-semibold">{tier}</span>
              <span className="text-[12.5px] text-ink-4">correct</span>
            </TCell>
            {segments.map((segment) => {
              const cell = matrix[segment]?.[tier] ?? null;
              const segmentLabel = segment === 'default' ? 'Default' : segmentRegistry[segment];
              const summary = cell ? `${cell.type} ${formatRewardValue(cell)}`.trim() : 'empty';
              const content = <CellContent cell={cell} emptyLabel={emptyLabel} />;
              return (
                <TCell key={segment}>
                  {onEditCell ? (
                    <button
                      type="button"
                      aria-label={`${tier} correct — ${segmentLabel} — ${summary}`}
                      className="flex w-full flex-col items-start gap-1 rounded-md p-1 text-left hover:bg-surface-muted"
                      onClick={() => onEditCell(segment, tier)}
                    >
                      {content}
                    </button>
                  ) : (
                    <span
                      aria-label={`${tier} correct — ${segmentLabel} — ${summary}`}
                      className="flex w-full flex-col items-start gap-1 p-1"
                    >
                      {content}
                    </span>
                  )}
                </TCell>
              );
            })}
          </TRow>
        ))}
      </Table>
      <p className="m-0 font-ui text-[12.5px] text-ink-4">{hint}</p>
    </Card>
  );
}

/** A cell's rendering: the Reward type as a pill plus its value, or the unset affordance. An
 * unrecognised `type` still shows as text — the jsonb column can hold a type this build predates. */
function CellContent({
  cell,
  emptyLabel,
}: {
  readonly cell: RewardCell | null;
  readonly emptyLabel: string;
}) {
  if (!cell) {
    return (
      <span className="inline-flex items-center gap-1.5 font-ui text-[12.5px] text-warn">
        <span className="inline-block h-[7px] w-[7px] rounded-pill bg-warn" aria-hidden />
        {emptyLabel}
      </span>
    );
  }
  const rewardType = toRewardType(cell.type);
  return (
    <>
      {rewardType ? (
        <RewardPill type={rewardType} />
      ) : (
        <span className="font-ui text-[12.5px] font-semibold text-ink-2">{cell.type}</span>
      )}
      <span className="font-mono text-[12.5px] text-ink-2">{formatRewardValue(cell)}</span>
    </>
  );
}

export interface PrizeCellModalProps {
  readonly segmentLabel: string;
  readonly tier: string;
  readonly cell: RewardCell | null;
  readonly onClose: () => void;
  readonly onSave: (cell: RewardCell | null) => void;
}

const REWARD_TYPE_OPTIONS = [
  { value: 'FreeBet', label: 'Bonus Bet' },
  { value: 'ProfitBoost', label: 'Profit Boost' },
  { value: 'ParlayEscalator', label: 'Parlay Escalator' },
];

/**
 * What a ProfitBoost cell must carry to be saveable, as operator-facing problems — empty when good.
 * A half-filled numeric input (`-`, `1.`) coerces to `NaN`, which serialises to `null` in the stored
 * matrix and can ship as `value`/`maxReward: null` in the award payload; guarding here is the same
 * protection `escalatorProblems` gives the escalator's rungs. The boost percent is required; the two
 * caps are optional but, when filled, must be finite.
 */
function profitBoostProblems(value: string, maxStake: string, maxReward: string): string[] {
  const problems: string[] = [];
  const finite = (input: string) => Number.isFinite(Number(input));
  if (value.trim() === '' || !finite(value)) problems.push('Boost percent must be a number.');
  if (maxStake.trim() !== '' && !finite(maxStake)) problems.push('Max stake must be a number.');
  if (maxReward.trim() !== '' && !finite(maxReward)) problems.push('Max reward must be a number.');
  return problems;
}

/** The prize-tier modal (design: context "Prize tier", Select reward type, Amount / Max stake).
 * A ParlayEscalator ships as Caesars' `TieredProfitBoost` and carries its own config, which
 * `ParlayEscalatorFields` owns end to end (CAE-564). Clearing a set cell saves `null`: a
 * correct-count with no cell is a non-paying tier (GL-07), not a zero reward. */
export function PrizeCellModal({ segmentLabel, tier, cell, onClose, onSave }: PrizeCellModalProps) {
  const [type, setType] = useState(cell?.type ?? 'FreeBet');
  const [amount, setAmount] = useState(cell?.amount != null ? String(cell.amount) : '');
  const [value, setValue] = useState(cell?.value != null ? String(cell.value) : '');
  const [maxStake, setMaxStake] = useState(cell?.maxStake != null ? String(cell.maxStake) : '');
  const [maxReward, setMaxReward] = useState(cell?.maxReward != null ? String(cell.maxReward) : '');
  const [escalator, setEscalator] = useState<EscalatorDraft>(() => toEscalatorDraft(cell));
  const [problems, setProblems] = useState<string[]>([]);

  function save() {
    if (type === 'FreeBet') {
      onSave({ type, amount: Number(amount) });
    } else if (type === 'ProfitBoost') {
      const found = profitBoostProblems(value, maxStake, maxReward);
      setProblems(found);
      if (found.length > 0) return;
      onSave({
        type,
        value: Number(value),
        ...(maxStake ? { maxStake: Number(maxStake) } : {}),
        ...(maxReward ? { maxReward: Number(maxReward) } : {}),
      });
    } else {
      const found = escalatorProblems(escalator);
      setProblems(found);
      if (found.length === 0) onSave(toEscalatorCell(escalator));
    }
  }

  return (
    <Modal open onClose={onClose} maxWidth={460}>
      <ModalHead
        context="Prize tier"
        title={`${tier} correct · ${segmentLabel}`}
        subtitle="One reward is delivered to every entry that lands in this tier."
        onClose={onClose}
      />
      <ModalBody>
        <div className="grid grid-cols-2 gap-x-5 gap-y-4 px-7 py-5">
          <Select
            className="col-span-2"
            label="Reward type"
            options={REWARD_TYPE_OPTIONS}
            value={type}
            onChange={setType}
          />
          {type === 'FreeBet' && (
            <Input
              label="Amount"
              mono
              type="number"
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
            />
          )}
          {type === 'ProfitBoost' && problems.length > 0 && (
            <div role="alert" className="col-span-2 flex flex-col gap-1">
              {problems.map((problem) => (
                <span key={problem} className="font-ui text-[12.5px] text-danger">
                  {problem}
                </span>
              ))}
            </div>
          )}
          {type === 'ProfitBoost' && (
            <Input
              label="Amount"
              mono
              type="number"
              hint="Boost percent"
              value={value}
              // Any edit clears the problems, as on the escalator: leaving them up while the operator
              // fixes the named field reads as though the fix didn't take. The next save recomputes.
              onChange={(event) => {
                setValue(event.target.value);
                setProblems([]);
              }}
            />
          )}
          {type !== 'ParlayEscalator' && (
            <Input
              label="Max stake"
              mono
              type="number"
              placeholder="—"
              disabled={type === 'FreeBet'}
              value={type === 'ProfitBoost' ? maxStake : ''}
              onChange={(event) => {
                setMaxStake(event.target.value);
                setProblems([]);
              }}
            />
          )}
          {type === 'ProfitBoost' && (
            <Input
              label="Max reward"
              mono
              type="number"
              placeholder="—"
              value={maxReward}
              onChange={(event) => {
                setMaxReward(event.target.value);
                setProblems([]);
              }}
            />
          )}
          {type === 'ParlayEscalator' && (
            <ParlayEscalatorFields
              draft={escalator}
              // Any edit clears the problems: leaving them up while the operator fixes the field
              // they name reads as though the fix did not take. The next save recomputes them.
              onChange={(next) => {
                setEscalator(next);
                setProblems([]);
              }}
              problems={problems}
            />
          )}
        </div>
      </ModalBody>
      <ModalFoot
        summary={
          <>
            Segment <strong className="font-semibold text-ink">{segmentLabel}</strong>
          </>
        }
      >
        {cell && (
          <Button variant="ghost" className="text-danger" onClick={() => onSave(null)}>
            Clear tier
          </Button>
        )}
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={save}>Save tier</Button>
      </ModalFoot>
    </Modal>
  );
}
